/**
 * The one data-fetching hook the feature screens use.
 *
 * It exists so every screen gets the same three states — loading, ready, error
 * — with the same rules: an in-flight request is aborted when its inputs change
 * or the screen unmounts, an abort is never shown as an error, and data already
 * on screen survives a refetch so a list does not blink on every keystroke.
 *
 * The error is kept as `unknown` rather than narrowed here: `errorMessage` and
 * `isApiError` in `lib/api/errors` are what turn it into something a person can
 * read, and they already handle a value that is not an `ApiError` at all.
 */
import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';

import { isAbortError } from './api/abort';

export type AsyncStatus = 'loading' | 'ready' | 'error';

interface AsyncState<T> {
  readonly status: AsyncStatus;
  readonly data: T | null;
  readonly error: unknown;
}

export interface AsyncData<T> extends AsyncState<T> {
  /** True while a reload runs over data that is still on screen. */
  readonly refreshing: boolean;
  /** True only for the very first load, when there is nothing to show yet. */
  readonly loadingFirst: boolean;
  readonly reload: () => void;
}

export function useAsyncData<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: DependencyList,
): AsyncData<T> {
  const [state, setState] = useState<AsyncState<T>>({
    status: 'loading',
    data: null,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  // `load` is a fresh closure every render; keeping the latest one in a ref lets
  // callers write it inline while `deps` alone decides when to re-fetch.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const controller = new AbortController();

    setState((previous) => ({ status: 'loading', data: previous.data, error: null }));

    loadRef.current(controller.signal).then(
      (data) => {
        if (controller.signal.aborted) return;
        setState({ status: 'ready', data, error: null });
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setState((previous) => ({ status: 'error', data: previous.data, error }));
      },
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller owns the dependency list
  }, [...deps, attempt]);

  const reload = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  return {
    ...state,
    refreshing: state.status === 'loading' && state.data !== null,
    loadingFirst: state.status === 'loading' && state.data === null,
    reload,
  };
}
